import type { CarrierRateRow } from './freight-rate-card-types';
import type { LtlQuote } from '../../types/ltl';

export const RATE_SORT_OPTION = {
  PriceAscending: 'price-asc',
  PriceDescending: 'price-desc',
  TransitAscending: 'transit-asc',
  TransitDescending: 'transit-desc',
} as const;
export type RateSortOption = (typeof RATE_SORT_OPTION)[keyof typeof RATE_SORT_OPTION];

export const DEFAULT_RATE_SORT_OPTION: RateSortOption = RATE_SORT_OPTION.PriceAscending;

type RowValueSelector = (quote: LtlQuote) => number;

const getPrice: RowValueSelector = (quote) => quote.totalCharges;
const getTransitDays: RowValueSelector = (quote) => quote.transitDays;

// The backend coerces a missing price/transit time to 0 (`?? 0`) rather than omitting
// the field, so 0 means "unknown", not "free" or "same-day". Excluding it here keeps
// such offers out of the top position when sorting ascending.
const isUsableValue = (value: number): boolean => Number.isFinite(value) && value > 0;

const getRowValue = (row: CarrierRateRow, selectValue: RowValueSelector, isAscending: boolean): number | null => {
  const values = [row.standard, row.guaranteed]
    .filter((slot): slot is LtlQuote => slot !== null)
    .map(selectValue)
    .filter(isUsableValue);

  if (values.length === 0) return null;
  return isAscending ? Math.min(...values) : Math.max(...values);
};

type SortConfig = {
  selectValue: RowValueSelector;
  isAscending: boolean;
};

const SORT_CONFIG: Record<RateSortOption, SortConfig> = {
  [RATE_SORT_OPTION.PriceAscending]: { selectValue: getPrice, isAscending: true },
  [RATE_SORT_OPTION.PriceDescending]: { selectValue: getPrice, isAscending: false },
  [RATE_SORT_OPTION.TransitAscending]: { selectValue: getTransitDays, isAscending: true },
  [RATE_SORT_OPTION.TransitDescending]: { selectValue: getTransitDays, isAscending: false },
};

/**
 * Sorts carrier rows by the best (min for ascending, max for descending) of their
 * standard/guaranteed slot values. Rows with no usable value on either slot sort last,
 * regardless of direction. Ties preserve the input order (stable sort), which keeps
 * output deterministic given `groupRates`' first-seen carrier order.
 */
export const sortCarrierRows = (rows: CarrierRateRow[], sortOption: RateSortOption): CarrierRateRow[] => {
  const { selectValue, isAscending } = SORT_CONFIG[sortOption];

  return [...rows].sort((rowA, rowB) => {
    const valueA = getRowValue(rowA, selectValue, isAscending);
    const valueB = getRowValue(rowB, selectValue, isAscending);

    if (valueA === null) return valueB === null ? 0 : 1;
    if (valueB === null) return -1;
    if (valueA === valueB) return 0;

    return isAscending ? valueA - valueB : valueB - valueA;
  });
};
